import type { DocumentReference, Firestore } from 'firebase/firestore';
import { runTransaction } from 'firebase/firestore';
import type { CommentReactionsMap } from '../types';
import {
  commentAuthorIdFromData,
  recordTaskCommentReactionEvent,
  shouldRecordCommentReactionEvent,
} from './commentReactionEvents';

const MAX_EMOJI_KEY_LEN = 32;

export function normalizeCommentReactions(raw: unknown): CommentReactionsMap {
  if (!raw || typeof raw !== 'object') return {};
  const out: CommentReactionsMap = {};
  for (const [emoji, ids] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof emoji !== 'string' || emoji.length > MAX_EMOJI_KEY_LEN) continue;
    if (Array.isArray(ids)) {
      const list = ids.filter((x): x is string => typeof x === 'string' && x.length > 0);
      if (list.length > 0) out[emoji] = list;
      continue;
    }
    // Defensive: some older clients wrote map-shaped "arrays" ({ "0": "uid" }).
    if (ids && typeof ids === 'object') {
      const list = Object.values(ids as Record<string, unknown>).filter(
        (x): x is string => typeof x === 'string' && x.length > 0
      );
      if (list.length > 0) out[emoji] = list;
    }
  }
  return out;
}

/** Pure toggle used by Firestore transactions (normalized map in → map out). */
export function applyReactionToggle(
  reactions: CommentReactionsMap,
  emoji: string,
  userId: string
): CommentReactionsMap {
  const key = emoji.trim();
  if (!key || key.length > MAX_EMOJI_KEY_LEN || !userId) return reactions;
  const out: CommentReactionsMap = { ...reactions };
  const list = [...(out[key] || [])];
  const idx = list.indexOf(userId);
  if (idx >= 0) {
    list.splice(idx, 1);
    if (list.length === 0) delete out[key];
    else out[key] = list;
  } else {
    out[key] = [...list, userId];
  }
  return out;
}

export type ToggleCommentReactionResult = {
  added: boolean;
  authorId: string;
  data: Record<string, unknown>;
};

export type ToggleCommentReactionOptions = {
  fallbackAuthorId?: string;
  projectLabel?: string | null;
};

function stringField(data: Record<string, unknown>, key: string): string | null {
  const value = data[key];
  return typeof value === 'string' ? value : null;
}

/**
 * Toggle current user's reaction for one emoji on a comment document.
 * Writes `reactions`, then logs a header event on add (not remove / self-react).
 */
export async function toggleCommentReaction(
  db: Firestore,
  commentRef: DocumentReference,
  emoji: string,
  userId: string,
  options?: ToggleCommentReactionOptions,
): Promise<ToggleCommentReactionResult> {
  const key = emoji.trim();
  if (!key || key.length > MAX_EMOJI_KEY_LEN || !userId) {
    throw new Error('Invalid reaction toggle (emoji or user missing)');
  }

  let result: ToggleCommentReactionResult = { added: false, authorId: '', data: {} };
  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(commentRef);
    if (!snap.exists()) {
      throw new Error('Comment no longer exists');
    }
    const data = (snap.data() || {}) as Record<string, unknown>;
    const before = normalizeCommentReactions(data.reactions);
    const after = applyReactionToggle(before, emoji, userId);
    const had = (before[key] || []).includes(userId);
    const has = (after[key] || []).includes(userId);
    result = {
      added: !had && has,
      authorId: commentAuthorIdFromData(data, options?.fallbackAuthorId),
      data,
    };
    transaction.update(commentRef, { reactions: after });
  });

  const authorId = result.authorId || String(options?.fallbackAuthorId || '').trim();
  result = { ...result, authorId };
  if (
    shouldRecordCommentReactionEvent({
      added: result.added,
      actorId: userId,
      authorId,
    })
  ) {
    try {
      await recordTaskCommentReactionEvent(db, {
        actorId: userId,
        emoji: key,
        commentId: commentRef.id,
        authorId,
        comment: {
          body: stringField(result.data, 'body'),
          bodyHtml: stringField(result.data, 'bodyHtml'),
          text: stringField(result.data, 'text'),
          content: stringField(result.data, 'content'),
          projectId: stringField(result.data, 'projectId'),
          groupCommentKey: stringField(result.data, 'groupCommentKey'),
          dealId: stringField(result.data, 'dealId'),
          audience: stringField(result.data, 'audience'),
          documentId: stringField(result.data, 'documentId'),
          leadId: stringField(result.data, 'leadId'),
          taskId: stringField(result.data, 'taskId'),
          applicationId: stringField(result.data, 'applicationId'),
          requestId: stringField(result.data, 'requestId'),
        },
        commentPath: commentRef.path,
        projectLabel: options?.projectLabel,
      });
    } catch (eventError) {
      console.error('Error recording reaction event:', eventError);
    }
  }
  return result;
}
